var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useCallback, useEffect, useState } from 'react';
import shortid from 'shortid';
import styled from '../../../styled';
import { Title, Message, MessageWrapper } from '../../input';
import DateInput from '../date/Input/DateInput';
import { Container, CalendarContainer } from '../date/DatePicker';
import dayjs, { isValidDateStr } from '../date/day';
import { getDefaultPlaceholder, getDefaultClearTooltip, getDefaultInputTooltip, getCalendarBottomPosition, } from '../date';
import Calendar from './Calendar';
import { HOUR_FORMAT as DEFAULT_HOUR_FORMAT } from '.';
export const DateTimeCalendarContainer = styled(CalendarContainer) `
  padding: 0 0 0 1rem;
`;
const DateTimePicker = (_a) => {
    var { lang = 'en', format = 'YYYY-MM-DD', inputRef = React.createRef(), inputWidth = 9.375, status = 'valid', title, renderMessages = [], block = false, disabled, value, defaultValue, placeholder, onDateChange, disabledDate, minuteInterval, customHourFormat, calendarAlign, calendarPosition = 'BOTTOM' } = _a, restProps = __rest(_a, ["lang", "format", "inputRef", "inputWidth", "status", "title", "renderMessages", "block", "disabled", "value", "defaultValue", "placeholder", "onDateChange", "disabledDate", "minuteInterval", "customHourFormat", "calendarAlign", "calendarPosition"]);
    const containerRef = useRef(null);
    const titleRef = useRef(null);
    const HOUR_FORMAT = customHourFormat || DEFAULT_HOUR_FORMAT;
    const [open, setOpen] = useState(false); // indicate that calendar is opened or not
    const [inputValue, setInputValue] = useState(defaultValue
        ? dayjs(lang, defaultValue).format(`${format} ${HOUR_FORMAT}`)
        : '');
    const [selectedDate, setSelectedDate] = useState(defaultValue ? dayjs(lang, defaultValue) : undefined);
    const date = value ? dayjs(lang, value) : selectedDate;
    const handleDateChange = useCallback((newDate) => {
        if (typeof inputRef === 'function' || !inputRef || !inputRef.current)
            return;
        const newValidDate = dayjs(lang, inputRef.current.value, `${format} ${HOUR_FORMAT}`);
        const isValidDate = isValidDateStr(inputRef.current.value, `${format} ${HOUR_FORMAT}`) &&
            !(disabledDate && disabledDate(newValidDate.toDate()));
        if (!isValidDate) {
            setSelectedDate(newDate);
            setInputValue(newDate.format(format));
            return;
        }
        if (dayjs(lang, inputValue, `${format} ${HOUR_FORMAT}`).isValid()) {
            const validHourDate = newDate.clone().hour(newValidDate.hour());
            setSelectedDate(validHourDate);
            setInputValue(validHourDate.format(`${format} ${HOUR_FORMAT}`));
            if (onDateChange)
                onDateChange({ date: validHourDate.toDate() });
            setOpen(false);
        }
        else {
            setInputValue(newValidDate.format(format));
        }
    }, [inputRef, lang, format, disabledDate, inputValue, onDateChange]);
    const handleTimeChange = useCallback((newDate) => {
        setSelectedDate(newDate);
        setInputValue(newDate.format(`${format} ${HOUR_FORMAT}`));
        if (onDateChange)
            onDateChange({ date: newDate.toDate() });
        setOpen(false);
    }, [format, onDateChange]);
    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value);
        const newDate = dayjs(lang, e.target.value, `${format} ${HOUR_FORMAT}`);
        const isValidDate = isValidDateStr(e.target.value, `${format} ${HOUR_FORMAT}`) &&
            !(disabledDate && disabledDate(newDate.toDate()));
        if (!isValidDate)
            return;
        setSelectedDate(newDate);
        if (onDateChange)
            onDateChange({ date: newDate.toDate() });
    }, [disabledDate, format, lang, onDateChange]);
    const handleClearBtnClick = useCallback(() => {
        setSelectedDate(undefined);
        setInputValue('');
        setOpen(true);
        if (onDateChange)
            onDateChange({ date: undefined });
        if (typeof inputRef === 'function' || !inputRef || !inputRef.current)
            return;
        inputRef.current.focus();
    }, [inputRef, onDateChange]);
    const handleInputClick = useCallback(() => setOpen(true), []);
    useEffect(() => {
        // update new valid date when input blur
        if (typeof inputRef === 'function' || !inputRef || !inputRef.current)
            return undefined;
        const inputElement = inputRef.current;
        const handleBlur = () => {
            // check date is a invalid format or disabled date
            const isValidDate = isValidDateStr(inputValue, `${format} ${HOUR_FORMAT}`) &&
                !(disabledDate &&
                    disabledDate(dayjs(lang, inputValue, `${format} ${HOUR_FORMAT}`).toDate()));
            if (!isValidDate) {
                // drop invalid range
                setSelectedDate(undefined);
                setInputValue('');
                if (onDateChange)
                    onDateChange({ date: undefined });
            }
            setOpen(false); // close calendar when blur
        };
        inputElement.addEventListener('blur', handleBlur);
        return () => {
            inputElement.removeEventListener('blur', handleBlur);
        };
    }, [disabledDate, format, inputRef, inputValue, lang, date, onDateChange]);
    useEffect(() => {
        setInputValue(value
            ? dayjs(lang, value).format(`${format} ${HOUR_FORMAT}`)
            : inputValue);
    }, [lang, format, value]); // eslint-disable-line react-hooks/exhaustive-deps
    return (React.createElement(Container, { block: block, ref: containerRef },
        title && (React.createElement(Title, { ref: titleRef, onClick: handleClearBtnClick }, title)),
        React.createElement(DateInput, Object.assign({ lang: lang, ref: inputRef, value: inputValue, width: inputWidth, block: block, status: status, disabled: disabled, placeholder: placeholder || getDefaultPlaceholder(lang), onChange: handleInputChange, onClick: handleInputClick, onCalendarIconClick: handleInputClick, onClearBtnClick: handleClearBtnClick, tooltipDirection: calendarPosition === 'TOP' ? 'BOTTOM_CENTER' : 'TOP_CENTER', tooltipText: getDefaultInputTooltip(lang).replace('{{format}}', `${format} Ham/pm`), clearBtnTooltipText: getDefaultClearTooltip(lang) }, restProps)),
        React.createElement(MessageWrapper, null, renderMessages.map(renderMessage => (React.createElement(Message, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() }, renderMessage())))),
        React.createElement(DateTimeCalendarContainer, { top: title ? 4.25 : 2.75, bottom: getCalendarBottomPosition({
                containerRef,
                titleRef,
            }), open: !disabled && open, onMouseDown: e => e.preventDefault(), calendarAlign: calendarAlign, calendarPosition: calendarPosition },
            React.createElement(Calendar, { lang: lang, format: `${format} ${HOUR_FORMAT}`, open: !disabled && open, selectedDate: date, value: inputValue, onDateChange: handleDateChange, onTimeChange: handleTimeChange, disabledDate: disabledDate, minuteInterval: minuteInterval }))));
};
export default React.memo(React.forwardRef((props, ref) => {
    if (typeof ref === 'function')
        throw new Error('ref callback is not supported');
    return React.createElement(DateTimePicker, Object.assign({ inputRef: ref || React.createRef() }, props));
}));
//# sourceMappingURL=DateTimePicker.js.map